import { cn } from '@repo/lib/cn';
import { Maximize, Minimize, Pause, Play, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { PlayerTime } from '@/components/ui/media-player';

import type { VideoPlayerControlsProps } from '.';

import { useMedia } from '..';

const buttonClassName: string = 'h-8 w-8 min-h-8 min-w-8 hover:bg-accent/50 active:bg-accent/80';
const buttonIconClassName: string = '!stroke-white size-3.5';

export function VideoPlayerControls(videoPlayerControlsProps: VideoPlayerControlsProps) {
  const { autoHide = true } = videoPlayerControlsProps;
  const { element, currentTime, duration, volume, isMuted, isFullscreen, isPlaying, currentPercentage, bufferedPercentage, togglePlay, seek, skip, toggleMute, toggleFullscreen } = useMedia();

  const [showControls, setShowControls] = useState<boolean>(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    if (autoHide) {
      hideTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1500);
    }
  };

  useEffect(() => {
    resetHideTimeout();

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [autoHide]);

  useEffect(() => {
    const elementCurrent = element.current;
    if (!elementCurrent) return;
    const controller = new AbortController();
    const handleMouseMove = () => {
      setShowControls(true);
      resetHideTimeout();
    };

    elementCurrent.addEventListener('mousemove', handleMouseMove, {
      signal: controller.signal,
    });

    return () => controller.abort();
  }, []);

  return (
    <div
      data-show={showControls}
      className={cn(
        'absolute right-0 bottom-0 left-0 bg-linear-to-t from-black/80 via-black/40 to-transparent',
        'pointer-events-none transition-all duration-300 data-[show=false]:h-0 data-[show=false]:opacity-0 data-[show=true]:h-fit data-[show=true]:opacity-100'
      )}
    >
      <div className="pointer-events-auto space-y-2 p-2 @md:p-3">
        {/* Progress Bar */}
        <div className="flex items-center gap-1.5 text-sm text-white">
          <PlayerTime type="current" />

          <div className="group/progress relative flex flex-1">
            <div
              className="absolute inset-0 h-1 rounded-full bg-white/50 transition-all duration-200"
              style={{
                width: `${duration > 0 ? bufferedPercentage : 0}%`,
              }}
            />
            <input
              type="range"
              min={0}
              step={0.1}
              max={duration || 0}
              value={currentTime}
              disabled={!showControls}
              onChange={(e) => {
                e.stopPropagation();
                seek(Number.parseFloat(e.target.value));
              }}
              className="[&::-webkit-slider-thumb]: z-10 h-1 w-full cursor-pointer appearance-none rounded-full bg-white/30 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-200 group-hover/progress:[&::-webkit-slider-thumb]:scale-125 md:[&::-webkit-slider-thumb]:h-3 md:[&::-webkit-slider-thumb]:w-3"
              style={{
                background: `linear-gradient(to right, #ffffff 0%, #ffffff ${currentPercentage}%, rgba(255,255,255,0.3) ${currentPercentage}%, rgba(255,255,255,0.3) 100%)`,
              }}
            />
          </div>
          <PlayerTime type="duration" />
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 @md:gap-2">
            <Button
              size="sm"
              variant={'ghost'}
              type={'button'}
              onClick={(e) => {
                e.stopPropagation();
                skip(-10);
              }}
              className={cn(buttonClassName, 'hidden @md:flex')}
            >
              <SkipBack className={buttonIconClassName} />
            </Button>
            <Button
              size="sm"
              variant={'ghost'}
              type={'button'}
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
              }}
              className={buttonClassName}
            >
              {isPlaying ? <Pause className={buttonIconClassName} /> : <Play className={cn(buttonIconClassName, 'ml-0.5')} />}
            </Button>
            <Button
              size="sm"
              variant={'ghost'}
              type={'button'}
              onClick={(e) => {
                e.stopPropagation();
                skip(10);
              }}
              className={cn(buttonClassName, 'hidden @md:flex')}
            >
              <SkipForward className="size-4 stroke-white!" />
            </Button>
            <Button
              size="sm"
              variant={'ghost'}
              type={'button'}
              disabled={!showControls}
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              className={buttonClassName}
            >
              {isMuted || volume === 0 ? <VolumeX className={buttonIconClassName} /> : <Volume2 className={buttonIconClassName} />}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={'ghost'}
              type={'button'}
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
              }}
              className={buttonClassName}
            >
              {isFullscreen ? <Minimize className={buttonIconClassName} /> : <Maximize className={buttonIconClassName} />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
